import { Injectable, inject } from '@angular/core';
import { FieldSelectors } from '@cropx-web/screens/field/state/field.state';
import {
  DatasetRoi,
  Measurement,
  SearchDataset,
} from '@cropx-web/services/api-gateway/MachineDataApiService';
import {
  MachineDataSelectors,
  MeasurementLayerWrapper,
} from '@cropx-web/state/machine-data.state';
import {
  Action,
  Selector,
  State,
  StateContext,
  Store,
  createPropertySelectors,
} from '@ngxs/store';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FieldMachineDataPage {
  const type = 'fieldMachineDataPage';

  export class SetActiveDataset {
    static readonly type = `[${type}] setActiveDataset`;
    constructor(public datasetUuid: string) {}
  }

  export class SetActiveMeasurement {
    static readonly type = `[${type}] setActiveMeasurement`;
    constructor(public measurementId: number) {}
  }
}

export interface FieldMachineDataPageModel {
  activeDatasetUuid: string;
  activeMeasurementId: number;
}

@State<FieldMachineDataPageModel>({
  name: 'fieldMachineDataPage',
  defaults: {
    activeDatasetUuid: null,
    activeMeasurementId: null,
  },
})
@Injectable()
export class FieldMachineDataPageState {
  private store = inject(Store);

  @Action(FieldMachineDataPage.SetActiveDataset)
  setActiveDataset(
    ctx: StateContext<FieldMachineDataPageModel>,
    action: FieldMachineDataPage.SetActiveDataset,
  ): void {
    const { datasetUuid } = action;
    const dataset = this.store.selectSnapshot(
      MachineDataSelectors.datasetByUuid(datasetUuid),
    );

    ctx.patchState({ activeDatasetUuid: datasetUuid });

    ctx.dispatch(
      new FieldMachineDataPage.SetActiveMeasurement(
        dataset._source.dataset_measurements[0].id,
      ),
    );
  }

  @Action(FieldMachineDataPage.SetActiveMeasurement)
  setActiveMeasurement(
    ctx: StateContext<FieldMachineDataPageModel>,
    action: FieldMachineDataPage.SetActiveMeasurement,
  ): void {
    ctx.patchState({ activeMeasurementId: action.measurementId });
  }
}

export class FieldMachineDataPageSelectors {
  static slices = createPropertySelectors<FieldMachineDataPageModel>(
    FieldMachineDataPageState,
  );

  @Selector([
    MachineDataSelectors.slices.datasets,
    FieldMachineDataPageSelectors.slices.activeDatasetUuid,
  ])
  static activeDataset(
    datasets: SearchDataset[],
    uuid: string,
  ): SearchDataset | undefined {
    return datasets.find((d) => d._source.uuid === uuid);
  }

  @Selector([
    FieldMachineDataPageSelectors.activeDataset,
    FieldMachineDataPageSelectors.slices.activeMeasurementId,
  ])
  static activeMeasurement(dataset: SearchDataset, id: number): Measurement {
    if (!dataset) {
      return null;
    }
    return dataset._source.dataset_measurements.find((m) => m.id === id);
  }

  @Selector([
    FieldMachineDataPageSelectors.activeDataset,
    FieldSelectors.slices.fieldUuid,
  ])
  static roi(
    dataset: SearchDataset | undefined,
    fieldUuid: string,
  ): DatasetRoi | null {
    if (!dataset) {
      return null;
    }
    return dataset._source.data_set_rois.find((r) => r.roi === fieldUuid);
  }

  @Selector([
    MachineDataSelectors.slices.measurementLayers,
    FieldMachineDataPageSelectors.slices.activeMeasurementId,
    FieldMachineDataPageSelectors.roi,
  ])
  static activeLayer(
    layers: MeasurementLayerWrapper[],
    measurementId: number,
    roi: DatasetRoi,
  ): MeasurementLayerWrapper {
    if (!roi) {
      return null;
    }

    return layers?.find(
      (l) => l.layerId === roi.id && l.measurementId === measurementId,
    );
  }
}
